import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Heart, Loader2, RefreshCw, UserMinus, UserPlus } from "lucide-react";
import OperatorConsoleShell from "../components/operator/OperatorConsoleShell";
import FollowerAnalyticsPanel from "../components/operator/FollowerAnalyticsPanel";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  clearHouseFollowers,
  fetchHouseFollowers,
  fetchOperatorDashboard,
} from "../redux/operatorConsoleSlice";
import { formatRelative } from "../utils/bettorsDeskHelpers";
import { buildTimelineSections } from "../utils/activityHelpers";
import {
  TAB_FOLLOW,
  TAB_UNFOLLOW,
  buildNetGrowthSeries,
  computeChurnStats,
  filterFollowerRows,
  followerEventDateKey,
  formatFollowerCount,
  isUnfollowAction,
  normalizeFollowerPulse,
} from "../utils/followersDeskHelpers";
import "../bettors-desk.css";
import "../audience-desks.css";

const FollowersDesk = () => {
  const { houseId } = useParams();
  const dispatch = useDispatch();
  const userId = useSelector(selectAppUser)?._id;

  const {
    houses,
    dashboard,
    houseFollowers,
    houseFollowersPagination,
    houseFollowersPulse,
    houseFollowersStatus,
    houseFollowersError,
    loadingMoreHouseFollowers,
  } = useSelector((s) => s.operatorConsole);

  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_FOLLOW);

  const bet = useMemo(
    () => houses.find((h) => String(h._id) === String(houseId)),
    [houses, houseId]
  );

  useEffect(() => {
    if (houseId) dispatch(fetchOperatorDashboard(houseId));
  }, [dispatch, houseId]);

  const listParams = useMemo(
    () => ({
      houseId,
      operatorUserId: userId,
      page: 1,
      limit: 40,
      append: false,
    }),
    [houseId, userId]
  );

  const reload = useCallback(() => {
    if (!houseId || !userId) return;
    dispatch(fetchHouseFollowers(listParams));
  }, [dispatch, houseId, userId, listParams]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    return () => dispatch(clearHouseFollowers());
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        dispatch(fetchHouseFollowers(listParams)).unwrap(),
        dispatch(fetchOperatorDashboard(houseId)).unwrap(),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const handleLoadMore = () => {
    if (
      !houseId ||
      !userId ||
      loadingMoreHouseFollowers ||
      !houseFollowersPagination?.hasNextPage
    ) {
      return;
    }
    dispatch(
      fetchHouseFollowers({
        houseId,
        operatorUserId: userId,
        page: (houseFollowersPagination.page || 1) + 1,
        limit: houseFollowersPagination.limit || 40,
        append: true,
      })
    );
  };

  const filteredRows = useMemo(
    () => filterFollowerRows(houseFollowers, activeTab),
    [houseFollowers, activeTab]
  );

  const sections = useMemo(
    () => buildTimelineSections(filteredRows, followerEventDateKey),
    [filteredRows]
  );

  const pulseLoaded =
    houseFollowersStatus === "succeeded" && houseFollowersPulse != null;

  const pulse = useMemo(
    () => normalizeFollowerPulse(houseFollowersPulse),
    [houseFollowersPulse]
  );

  const pulseLoading =
    houseFollowersStatus === "loading" && !houseFollowers.length && !pulseLoaded;

  const netGrowthSeries = useMemo(
    () => buildNetGrowthSeries(pulse.dailyTrend),
    [pulse.dailyTrend]
  );

  const churnStats = useMemo(
    () => computeChurnStats(pulse, netGrowthSeries),
    [pulse, netGrowthSeries]
  );

  const headerSubtitle = useMemo(() => {
    const houseName = bet?.name || dashboard?.houseName || "House";
    if (houseFollowersStatus === "loading" && !houseFollowers.length) {
      return houseName;
    }
    if (!pulseLoaded) {
      return `${houseName} · follow activity`;
    }
    const net24 = (Number(pulse.followedLast24h) || 0) - (Number(pulse.unfollowedLast24h) || 0);
    const netLabel = net24 > 0 ? `+${net24}` : net24 < 0 ? `${net24}` : "0";
    return `${houseName} · ${formatFollowerCount(pulse.currentFollowers)} · ${netLabel} net 24h`;
  }, [
    bet?.name,
    dashboard?.houseName,
    houseFollowersStatus,
    houseFollowers.length,
    pulseLoaded,
    pulse.currentFollowers,
    pulse.followedLast24h,
    pulse.unfollowedLast24h,
  ]);

  const isUnfollowTab = activeTab === TAB_UNFOLLOW;

  if (!houseId) {
    return (
      <OperatorConsoleShell activeSection="followers" mainClassName="oc-desk-main">
        <div className="bd-page bd-followers-desk">
          <div className="bd-loading">Missing house.</div>
        </div>
      </OperatorConsoleShell>
    );
  }

  return (
    <OperatorConsoleShell activeSection="followers" mainClassName="oc-desk-main">
      <div className="bd-page bd-followers-desk">
      <header className="bd-header bd-followers-header">
        <div className="bd-header-copy">
          <p className="bd-eyebrow">
            <Heart size={9} />
            Follower desk
          </p>
          <span className="bd-header-title">
            {headerSubtitle}
            {pulseLoaded
              ? isUnfollowTab
                ? ` · ${pulse.unfollowedLast24h} unfollows 24h`
                : ` · +${pulse.followedLast24h} follows 24h`
              : ""}
          </span>
        </div>
        <button
          type="button"
          className="bd-icon-btn"
          onClick={handleRefresh}
          disabled={refreshing || houseFollowersStatus === "loading"}
          aria-label="Refresh"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
        </button>
      </header>

      <div className="bd-desk-body">
      <div className="bd-bettors-command bd-followers-command">
        <aside className="bd-roster-rail">
          <div className="bd-roster-toolbar">
            <div className="bd-chip-row bd-chip-row-compact" role="tablist" aria-label="Follower events">
              <button
                type="button"
                role="tab"
                aria-selected={!isUnfollowTab}
                className={`bd-chip ${!isUnfollowTab ? "bd-chip-active" : ""}`}
                onClick={() => setActiveTab(TAB_FOLLOW)}
              >
                <UserPlus size={10} aria-hidden />
                Follows
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isUnfollowTab}
                className={`bd-chip ${isUnfollowTab ? "bd-chip-active" : ""}`}
                onClick={() => setActiveTab(TAB_UNFOLLOW)}
              >
                <UserMinus size={10} aria-hidden />
                Unfollows
              </button>
            </div>
          </div>

          <section className="bd-roster bd-roster-rail-scroll">
            <header className="bd-rail-head">
              <h2>{isUnfollowTab ? "Unfollows" : "Follows"}</h2>
              <span className="tabular-nums">{filteredRows.length}</span>
            </header>

            {houseFollowersStatus === "loading" && !houseFollowers.length && (
              <div className="bd-loading bd-loading-compact">
                <Loader2 size={18} className="animate-spin" />
                <span>Loading…</span>
              </div>
            )}

            {houseFollowersError && !houseFollowers.length && (
              <div className="bd-loading bd-loading-compact">
                <p>{String(houseFollowersError)}</p>
                <button type="button" onClick={reload}>
                  Retry
                </button>
              </div>
            )}

            {!filteredRows.length && houseFollowersStatus === "succeeded" && (
              <p className="bd-empty bd-empty-compact">
                {isUnfollowTab ? "No unfollow events yet." : "No follow events yet."}
              </p>
            )}

            {sections.map((section) => (
              <div key={section.dateKey} className="bd-day">
                <h3 className="bd-day-head">
                  <span>{section.title}</span>
                  <span className="bd-day-line" aria-hidden />
                </h3>
                <ul className="bd-roster-list">
                  {section.data.map((row, index) => {
                    const unfollow = isUnfollowAction(row);
                    const when = row.occurredAt;
                    return (
                      <li key={`${row.userId}-${row.action}-${when}-${index}`}>
                        <article className="bd-roster-row bd-viewer-row">
                          <span className="bd-roster-avatar">
                            {row.username?.[0]?.toUpperCase() || "?"}
                          </span>
                          <div className="bd-roster-copy">
                            <strong>{row.username || "Follower"}</strong>
                          </div>
                          <div className="bd-roster-side bd-viewer-side">
                            <span
                              className={`bd-viewer-tag ${
                                unfollow ? "bd-follower-tag-unfollow" : "bd-follower-tag-follow"
                              }`}
                            >
                              {unfollow ? "Unfollow" : "Follow"}
                            </span>
                            <em className="tabular-nums">{when ? formatRelative(when) : "—"}</em>
                          </div>
                        </article>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {houseFollowersPagination?.hasNextPage ? (
              <button
                type="button"
                className="bd-load-more bd-load-more-compact"
                onClick={handleLoadMore}
                disabled={loadingMoreHouseFollowers}
              >
                {loadingMoreHouseFollowers ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    Loading…
                  </>
                ) : (
                  "Load more"
                )}
              </button>
            ) : null}
          </section>
        </aside>

        <div className="bd-bettors-workspace">
          <FollowerAnalyticsPanel
            layout="workspace"
            pulse={pulse}
            loading={pulseLoading}
            activeTab={activeTab}
            netGrowthSeries={netGrowthSeries}
            churnStats={churnStats}
          />
        </div>
      </div>
      </div>
      </div>
    </OperatorConsoleShell>
  );
};

export default FollowersDesk;
